import { useState, useEffect, useCallback, Fragment } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { auditApi } from '@/api';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ACTION_TABS = [
  { key: 'tous', label: 'Tout', actions: null },
  { key: 'connexions', label: 'Connexions', actions: 'login,login_failed' },
  { key: 'creations', label: 'Créations', actions: 'create' },
  { key: 'modifications', label: 'Modifications', actions: 'update,status_change' },
  { key: 'suppressions', label: 'Suppressions', actions: 'delete' },
  {
    key: 'rapports',
    label: 'Rapports',
    actions: 'generate_report,delete_rapport_version,rapport_brouillon,rapport_definitive',
  },
];

const ENTITY_TYPES = [
  { value: '', label: 'Toutes les entités' },
  { value: 'dossier', label: 'Dossier' },
  { value: 'client', label: 'Client' },
  { value: 'user', label: 'Utilisateur' },
];

const ACTION_BADGE_STYLES = {
  login: 'bg-blue-100 text-blue-700',
  login_failed: 'bg-red-100 text-red-700',
  create: 'bg-green-100 text-green-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
  status_change: 'bg-purple-100 text-purple-700',
  generate_report: 'bg-indigo-100 text-indigo-700',
  delete_rapport_version: 'bg-red-100 text-red-700',
};

function getActionBadgeClass(action) {
  if (ACTION_BADGE_STYLES[action]) return ACTION_BADGE_STYLES[action];
  if (action?.startsWith('rapport_')) return 'bg-indigo-100 text-indigo-700';
  return 'bg-slate-100 text-slate-700';
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm:ss', { locale: fr });
  } catch {
    return dateStr;
  }
}

function ChangesDetail({ changes }) {
  if (!changes || Object.keys(changes).length === 0) {
    return <span className="text-slate-400 italic">Pas de détail disponible</span>;
  }

  return (
    <dl className="space-y-1">
      {Object.entries(changes).map(([key, value]) => (
        <div key={key} className="flex gap-2 text-sm">
          <dt className="font-medium text-slate-700 min-w-30">{key} :</dt>
          <dd className="text-slate-600">
            {typeof value === 'object' && value !== null && ('old' in value || 'new' in value) ? (
              <>
                <span className="line-through text-red-500">{String(value.old ?? '—')}</span>
                {' → '}
                <span className="text-green-600">{String(value.new ?? '—')}</span>
              </>
            ) : (
              String(value)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('tous');
  const [entityType, setEntityType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { skip, limit };
      const tab = ACTION_TABS.find((t) => t.key === activeTab);
      if (tab?.actions) params.action = tab.actions;
      if (entityType) params.entity_type = entityType;
      const data = await auditApi.list(params);
      setLogs(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, activeTab, entityType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setSkip(0);
    setExpandedId(null);
  };

  const handleEntityChange = (e) => {
    setEntityType(e.target.value);
    setSkip(0);
    setExpandedId(null);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Client-side search filter on user_id display (email not available from API, but we show user_id)
  const filteredLogs = searchQuery
    ? logs.filter((log) => log.user_id?.toLowerCase().includes(searchQuery.toLowerCase()))
    : logs;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900">Journal d&apos;audit</h1>
            {!loading && (
              <Badge variant="default">
                {total} entrée{total !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <p className="text-slate-600 mt-2">
            Historique des actions effectuées dans l&apos;application
          </p>
        </div>

        {/* Search + Entity filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par identifiant utilisateur..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-carex-500 focus:border-transparent"
            />
          </div>
          <select
            value={entityType}
            onChange={handleEntityChange}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-carex-500 focus:border-transparent bg-white"
          >
            {ENTITY_TYPES.map((et) => (
              <option key={et.value} value={et.value}>
                {et.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action tabs */}
        <div className="flex gap-2 border-b border-slate-200 mb-6">
          {ACTION_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-carex-600 text-carex-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && <LoadingSpinner message="Chargement du journal..." />}

        {/* Error */}
        {error && <ErrorMessage message={error} onRetry={fetchLogs} />}

        {/* Empty */}
        {!loading && !error && filteredLogs.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <p className="text-slate-500 text-lg">Aucune entrée dans le journal.</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && filteredLogs.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="w-8 px-4 py-3" />
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Date</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Utilisateur</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Action</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Entité</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredLogs.map((log) => (
                  <Fragment key={log.id}>
                    <tr
                      onClick={() => toggleExpand(log.id)}
                      className="hover:bg-slate-50 transition cursor-pointer"
                    >
                      <td className="px-4 py-4 text-slate-400">
                        {expandedId === log.id ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                        {formatDateTime(log.created_at)}
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-mono text-xs">
                        {log.user_id ? log.user_id.slice(0, 8) + '...' : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadgeClass(log.action)}`}
                        >
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{log.entity_type || '—'}</td>
                      <td className="px-6 py-4 text-slate-400 font-mono text-xs">
                        {log.ip_address || '—'}
                      </td>
                    </tr>
                    {expandedId === log.id && (
                      <tr key={`${log.id}-detail`} className="bg-slate-50">
                        <td />
                        <td colSpan={5} className="px-6 py-4">
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <span className="font-medium text-slate-700">ID Utilisateur : </span>
                              <span className="font-mono text-slate-600">
                                {log.user_id || '—'}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">ID Entité : </span>
                              <span className="font-mono text-slate-600">
                                {log.entity_id || '—'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="font-medium text-slate-700 text-sm">
                              Changements :
                            </span>
                            <div className="mt-1 bg-white rounded border border-slate-200 p-3">
                              <ChangesDetail changes={log.changes} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && filteredLogs.length > 0 && (
          <Pagination
            total={total}
            skip={skip}
            limit={limit}
            onChange={(newSkip) => setSkip(newSkip)}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setSkip(0);
            }}
          />
        )}
      </div>
    </div>
  );
}
